/**
 * `<Toast>` — transient notification stack.
 *
 * Spec: design/post-walkthrough/global-patterns-spec.md §1.2.
 * Phase 1 (post-walkthrough) changes:
 *   - 4 variants: success / error / info / warning.
 *   - Status accent: 4px left border in status-{kind}-fg.
 *   - Per-variant auto-dismiss: 5s success/info, 8s warning, NEVER error.
 *   - Timer owned here (useEffect) — store no longer manages timeouts.
 *   - Hover-pause: mouse enter/leave toggles `paused` ref.
 *   - Action slot: optional inline action button.
 *   - Stack max 3 (enforced in store).
 *
 * Keyboard: Tab reaches the close button; Esc closes the focused toast.
 */
import { useEffect, useRef } from "react";
import { CheckCircle, AlertTriangle, Info, AlertCircle, X } from "lucide-react";
import {
  useToastStore,
  type Toast as ToastItem,
  type ToastVariant,
} from "@/stores/useToastStore";

// ── Per-variant config ────────────────────────────────────────────────────────

/** Auto-dismiss duration in ms. `null` = never (error). */
const DISMISS_MS: Record<ToastVariant, number | null> = {
  success: 5000,
  info:    5000,
  warning: 8000,
  error:   null,
};

/** Left border color class per variant (4px accent). */
const ACCENT_BORDER: Record<ToastVariant, string> = {
  success: "border-l-status-interviewed-fg", // teal
  error:   "border-l-status-rejected-fg",    // red
  info:    "border-l-status-active-fg",       // blue
  warning: "border-l-status-in-process-fg",  // amber
};

/** Icon component + color per variant. */
const ICON_CONFIG: Record<
  ToastVariant,
  { Icon: typeof CheckCircle; colorClass: string }
> = {
  success: { Icon: CheckCircle,    colorClass: "text-status-interviewed-fg" },
  error:   { Icon: AlertTriangle,  colorClass: "text-status-rejected-fg" },
  info:    { Icon: Info,           colorClass: "text-status-active-fg" },
  warning: { Icon: AlertCircle,    colorClass: "text-status-in-process-fg" },
};

// ── Single toast item component ───────────────────────────────────────────────

interface ToastCardProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

function ToastCard({ toast, onDismiss }: ToastCardProps): JSX.Element {
  const pausedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissMs = DISMISS_MS[toast.variant];
  const { Icon, colorClass } = ICON_CONFIG[toast.variant];

  // Schedule auto-dismiss (not for error).
  useEffect(() => {
    if (dismissMs === null) return;

    const schedule = (): void => {
      timerRef.current = setTimeout(() => {
        if (!pausedRef.current) {
          onDismiss(toast.id);
        } else {
          // Paused during hover — reschedule a short poll.
          schedule();
        }
      }, dismissMs);
    };

    schedule();

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast.id, dismissMs]);

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === "Escape") onDismiss(toast.id);
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      data-testid="toast-card"
      data-variant={toast.variant}
      className={`flex w-96 max-w-[calc(100vw-3rem)] items-start gap-3 rounded-lg border border-border-default border-l-4 bg-surface-elevated p-4 shadow-lg ${ACCENT_BORDER[toast.variant]}`}
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onKeyDown={handleKeyDown}
    >
      {/* Icon */}
      <span className={`mt-0.5 shrink-0 ${colorClass}`} aria-hidden="true">
        <Icon size={16} />
      </span>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text-primary">{toast.message}</p>
        {toast.action ? (
          <button
            type="button"
            onClick={toast.action.onClick}
            className="mt-1 text-xs font-semibold text-text-link hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-secondary)]"
          >
            {toast.action.label}
          </button>
        ) : null}
      </div>

      {/* Dismiss */}
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
        className="shrink-0 text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-secondary)] rounded-sm"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

// ── Stack container ───────────────────────────────────────────────────────────

export function Toast(): JSX.Element {
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);

  return (
    <div
      aria-label="Notifications"
      className="fixed bottom-6 right-6 z-50 flex flex-col gap-2"
    >
      {toasts.map((toast: ToastItem) => (
        <ToastCard key={toast.id} toast={toast} onDismiss={removeToast} />
      ))}
    </div>
  );
}
